// Filename: gnnodexuipart.js
// Timestamp: 2018.01.20-01:48:21 (last modified)
// Author(s): bumblehead <chris@bumblehead.com>
//
// special properties used with partial operations
//
// `full` are elements from a list of data stored on the parent node.
// from each full are generated document-friendly presentation values,
// stored on the node as `fullhydrated`.
//
// Each `fullhydrated` value includes a *unique key* used represent the
// value and other metadata such as *labels* and *sorted position* are also
// generated.

import {
  gngraphNodeSet as graphNodeSet
} from './gngraph.js'

import gnnodexui from './gnnodexui.js'

import {
  gnkey_docencodekeyarr
} from './gnkey.js'

import {
  gnerr_nodepartkeyfullinvalid,
  gnerr_nodepropselecttypeinvalid,
  gnerr_nodesubjvalueinvalid
} from './gnerr.js'

import {
  gnspec_clientis as specClientIs,
  gnspec_keyisvalid
} from './gnspec.js'

import {
  gnutilPlainObjIs as utilPlainObjIs,
  gnutilNonNullObjIs as utilNonNullObjIs
} from './gnutil.js'

import {
  gnaccSubj as accSubj,
  gnaccPart as accPart,
  gnaccFull as accFull,
  // gnaccBase as accBase,
  gnaccCache as accCache
} from './gnacc.js'

import {
  gnenumKEY as KEY,
  gnenumTYPE as TYPE,
  gnenumCHILD as CHILD,
  gnenumSELECTTYPESINGLE as SELECTTYPESINGLE,
  gnenumSELECTTYPEMULTIPLE as SELECTTYPEMULTIPLE,
  gnenumISPATCHREHYDRATE as ISPATCHREHYDRATE,
  gnenumPARTISOPTIMISED as PARTISOPTIMISED,
  gnenumPARTKEYDOC as PARTKEYDOC,
  gnenumPARTKEYNODE as PARTKEYNODE,
  gnenumPARTKEYFULLTYPE as PARTKEYFULLTYPE,
  gnenumPARTKEYFULL as PARTKEYFULL,
  gnenumPARTKEYSUBJ as PARTKEYSUBJ,
  gnenumPARTKEYPART as PARTKEYPART,
  gnenumPARTKEYRETURN as PARTKEYRETURN
} from './gnenum.js'

import {
  gnrun,
  gnrun_retDataWHERE
} from './gnrun.js'

import {
  gnutilNumOrStrIs as utilNumOrStrIs
} from './gnutil.js'

const partPARTKEYFULLIsValid = fullkey => (
  utilNumOrStrIs(fullkey))

const subjVALUEIsValid = (value, valuetype = typeof value) => (
  valuetype !== 'undefined')

const defaultsubjkeyspec = {
  [TYPE]: "client",
  name: PARTKEYSUBJ,
  chain: [
    ["nhylook", ["subj.value"]],
    ["default", ['']]]
}

const defaultpartkeyspec = {
  [TYPE]: "client",
  name: PARTKEYPART,
  chain: [
    ["part", [PARTKEYPART]],
    ["default", ['']]]
}

// parts spec may be defined to resolve individual properties
// or an entire object which holds the required properties
//  * if defined individually, return individual 'return' spec
//  * if defined wholly, return modified variant of spec
//    resolving the 'return' value
const specResolveReturnGet = nd => {
  const partspec = accPart.specget(nd)

  if (Array.isArray(partspec)) {
    // return partspec.find(sp => sp.name === PARTKEYRETURN)
    let bestspec
    for (let spec of partspec) {
      if (spec.name === PARTKEYRETURN) return spec
      if (spec.name === PARTKEYFULL) bestspec = spec
    }
    return bestspec
  } else if (specClientIs(partspec)) {
    return {
      [TYPE]: 'client',
      chain: partspec.chain.concat([
        // ['getField', [PARTKEYRETURN]]
        // ```
        // .do(part => part('return').default(part('fullkey')))`
        // ```
        ["do",[["gn:CHAIN","expr",[
          ["row",["gn:ARGSIG","expr",0,"part"]],
          ["row.asfn",[PARTKEYRETURN]],
          ["default",[["gn:CHAIN","part(ay1)",[
            ["row",["gn:ARGSIG","expr",0,"part"]],
            ["row.asfn",[PARTKEYFULL]]]]]]]]]]
      ])
    }
  } else {
    throw new Error('unhandled')
  }
}

const specResolvePartKeyFullGet = nd => {
  const partspec = accPart.specget(nd)

  if (Array.isArray(partspec)) {
    // return partspec.find(sp => sp.name === PARTKEYRETURN)
    let bestspec
    for (let spec of partspec) {
      if (spec.name === PARTKEYFULL) return spec
      if (spec.name === PARTKEYRETURN) bestspec = spec
    }
    return bestspec
  } else if (specClientIs(partspec)) {
    return {
      [TYPE]: 'client',
      chain: partspec.chain.concat([
        // ['getField', [PARTKEYRETURN]]
        // ```
        // .do(part => part('return').default(part('fullkey')))`
        // ```
        ["do",[["gn:CHAIN","expr",[
          ["row",["gn:ARGSIG","expr",0,"part"]],
          ["row.asfn",[PARTKEYFULL]],
          ["default",[["gn:CHAIN","part(ay1)",[
            ["row",["gn:ARGSIG","expr",0,"part"]],
            ["row.asfn",[PARTKEYRETURN]]]]]]]]]]
      ])
    }
  } else {
    throw new Error('unhandled')
  }
}

// 'optimised' nodes,
//  * they have "full" array that is a list of primitives
//  * they have no "part" values (nothing to dynamically resolve)
//  * each element in full array is a valid key, label and return value
const isoptimiseddetect = nd => {
  let isoptimised = accCache.propget(nd, PARTISOPTIMISED)
  if (typeof isoptimised === 'boolean')
    return isoptimised

  const part = accPart.specget(nd)
  const full = accFull.get(nd, [])

  return !part && !utilNonNullObjIs(full[0])
}

const xinit = node => gnnodexui.xinit(accCache.assign(node, {
  [ISPATCHREHYDRATE]: Boolean(node.get(CHILD, []).length),
  [PARTISOPTIMISED]: isoptimiseddetect(node)
}))

const xreset = nd => (
  nd = accPart.rm(nd),
  nd = accFull.rm(nd),
  gnnodexui.xreset(nd))

const isoptimised = nd => (
  accCache.propget(nd, PARTISOPTIMISED))

const partAtomSetKeyDoc = (nd, partatom) => {
  const keynode = nd.get(KEY)
  const keypart = partatom[PARTKEYPART]

  partatom[PARTKEYNODE] = keynode
  partatom[PARTKEYDOC] = gnkey_docencodekeyarr([keynode, keypart])

  return partatom
}

// returns an object usable by uipartial-nodes
//
// keyn should be better documented it is a keyname for
// the data which _can_ be combined with full node 'key'
// to return a unique key for the given partial
//
// not all partials need that
//
const partAtomCreateFromPartial = (cfg, gr, nd, part, isactive) => {
  const keyfull = part[PARTKEYFULL]

  if (!gnspec_keyisvalid(keyfull))
    throw gnerr_nodepartkeyfullinvalid(gr, nd, keyfull)

  return partAtomSetKeyDoc(nd, Object.assign({}, part, {
    // partkey: part.partkey || keyfull,
    partkey: keyfull,
    isactive: Boolean(isactive)
  }))
}

const partAtomCreateFromPrimitive = (cfg, gr, nd, val, isactive) => {
  const keyfull = val
  const keypart = val

  if (!gnspec_keyisvalid(keyfull))
    throw gnerr_nodepartkeyfullinvalid(gr, nd, keyfull)

  return partAtomSetKeyDoc(nd, {
    [PARTKEYFULL]: keyfull,
    [PARTKEYPART]: keypart,
    [PARTKEYRETURN]: val,
    label: val,
    isactive: Boolean(isactive)
  })
}

// map each part to fn and, if isdockey, generate dockey
//
const partAtomsMap = (nd, fn) => accPart.get(nd, []).map(fn)

// `partial` would be a single option in an input featuring multiple options
// eslint-disable-next-line max-len
// such as a dropdown. 'each requires a partkey, label and return value directives.
// 'partial' is a complete and selectable data
const selecttypeIsValid = selecttype => (
  selecttype === SELECTTYPESINGLE || selecttype === SELECTTYPEMULTIPLE)

const selecttypeGet = (cfg, gr, nd) => {
  const selecttype = accSubj.propget(nd, 'selecttype', SELECTTYPESINGLE)

  if (!selecttypeIsValid(selecttype))
    throw gnerr_nodepropselecttypeinvalid(cfg, gr, nd, selecttype)

  return selecttype
}

// resolve part atoms by applying PART spec to value(s) from FULL namespace
// see enums file for partkey descriptions
const partAtomFromFull = async (ss, cfg, gr, nd, full, subj) => {
  const partspec = accPart.specget(nd, [])
  const selecttype = selecttypeGet(cfg, gr, nd)
  const ns = full

  // `multiple`, provide an array of subj,
  // `single`, provide a single subj
  if (selecttype === SELECTTYPESINGLE) {
    subj = [subj]
  }

  const gnrunparts = async (ss, cfg, ns, partspecarr) => {
    const part = {}

    for (const partspec of partspecarr) {
      if (specClientIs(partspec)) {
        const partname = partspec.name

        part[partname] = await gnrun(ss, cfg, gr, nd, ns, partspec)
        if (partname === PARTKEYFULL
          && !partPARTKEYFULLIsValid(part[partname])
        ) {
          throw gnerr_nodepartkeyfullinvalid(gr, nd, part[partname])
        }
      } else if (utilPlainObjIs(partspec)) {
        Object.assign(part, partspec)
      }
    }

    return part
  }

  // convert part namespace using pattern to full 'part'
  const partns = Array.isArray(partspec)
    ? await gnrunparts(ss, cfg, ns, partspec)
    : await gnrun(ss, cfg, gr, nd, ns, partspec)

  if (!partPARTKEYFULLIsValid(partns[PARTKEYFULL])) {
    throw gnerr_nodepartkeyfullinvalid(gr, nd, partns[PARTKEYFULL])
  }

  const partnsresolved = PARTKEYRETURN in partns
    ? partns[PARTKEYRETURN]
    : partns[PARTKEYFULL]

  const state = {}
  await Promise.all(subj.map(async s => {
    const res = await gnrun(ss, cfg, gr, nd, { subj: s }, defaultsubjkeyspec)
    if (res === partnsresolved)
      state.isactive = true
  }))

  return partAtomCreateFromPartial(cfg, gr, nd, partns, state.isactive)
}

// quickly return active key val
// for simple data with mapping 1:1
const returnValFromFullAtom = async (ss, cfg, gr, nd, fullatom) => {
  if (isoptimised(nd))
    return fullatom

  const returnspec = specResolveReturnGet(nd)
  return returnspec.chain.length
    ? gnrun(ss, cfg, gr, nd, fullatom, returnspec)
    : fullatom
}

const returnValsFromFullAtoms = async (ss, cfg, gr, nd, fullAtoms) => {
  if (!fullAtoms.length)
    return fullAtoms

  return Promise.all(fullAtoms.map(part => (
    returnValFromFullAtom(ss, cfg, gr, nd, part))))
}

const partAtomsFromFullKeys = async (ss, cfg, gr, nd, fullKeys) => {
  const parts = accPart.get(nd)
  const keyfulltype = accCache.propget(nd, PARTKEYFULLTYPE)

  return gnrun_retDataWHERE(
    ss, cfg, gr, nd, fullKeys, parts, defaultpartkeyspec, keyfulltype)
}

const getsubjkeyspec = node => (
  accCache.propget(node, PARTKEYSUBJ, defaultsubjkeyspec))

const fullAtomsFromPartKeys = async (ss, cfg, gr, nd, partkeys) => {
  if (isoptimised(nd))
    return partkeys

  return gnrun_retDataWHERE(
    ss, cfg, gr, nd, partkeys, accFull.get(nd), specResolvePartKeyFullGet(nd))
}

// return value is resolved from base w/ filters
//
const returnValsFromFullKeys = async (ss, cfg, gr, nd, fullkeys) => {
  const partatoms = await partAtomsFromFullKeys(ss, cfg, gr, nd, fullkeys)
  const partatomskeys = partatoms.map(part => part[PARTKEYPART])
  const fullatoms = await fullAtomsFromPartKeys(ss, cfg, gr, nd, partatomskeys)

  return returnValsFromFullAtoms(ss, cfg, gr, nd, fullatoms)
}

// from active key array,
// return final values from corresponding full object(s)
const returnValsFromPartKeys = async (ss, cfg, gr, nd, ndx, partkeys) => {
  let retvals = await returnValsFromFullKeys(ss, cfg, gr, nd, partkeys)

  return selecttypeGet(cfg, gr, nd) === SELECTTYPESINGLE
    ? retvals[0]
    : retvals
}

const returnValFromPartKey = async (ss, cfg, gr, nd, ndx, partkey) => (
  ndx.returnValsFromPartKeys(ss, cfg, gr, nd, ndx, [partkey]))

const partAtomsFromFull = async (ss, cfg, gr, nd, ndx) => {
  const fullarr = accFull.get(nd, [])
  const subj = accSubj.get(nd, {})

  // TODO improve error message. why is value necessary?
  // consider ndxobjls scenario
  if (!subjVALUEIsValid(subj.value))
    throw gnerr_nodesubjvalueinvalid(gr, nd, subj.value)

  // where optimised,
  // - do not construct input partials
  // - assume input uses same definition for key/value/subj.
  // - do not construct labels and associations for each input
  // - useful for things like month/day/year dropdown
  // - numbers converted to string; because DOM form-controls return strings
  if (isoptimised(nd)) {
    const value = await gnrun(ss, cfg, gr, nd, {subj}, getsubjkeyspec(nd))

    return fullarr.map(elem => (
      partAtomCreateFromPrimitive(cfg, gr, nd, elem, value === elem)))
  }

  return Promise.all(fullarr.map(fullatom => (
    ndx.partAtomFromFull(ss, cfg, gr, nd, fullatom, subj))))
}

// optimised defines spec items as a user would,
// next some items are elided and moved to cache store
// to be used outside the default partials pipeline
const xhydratedsubj = async (ss, cfg, gr, nd, ndx) => {
  const partialarr = await partAtomsFromFull(ss, cfg, gr, nd, ndx)
  let res

  nd = accPart.set(nd, partialarr)

  if (partialarr[0]) {
    res = graphNodeSet(gr, accCache.propset(nd,
      PARTKEYFULLTYPE, typeof partialarr[0][PARTKEYFULL]))
    gr = res[0]
    nd = res[1]
  }

  res = graphNodeSet(gr, nd)
  gr = res[0]
  nd = res[1]

  return [gr, nd]
}

export default Object.assign({}, gnnodexui, {
  type: 'gnnodexuipart',

  xinit,
  xreset,
  xhydratedsubj,
  partAtomsMap,
  partAtomCreateFromPartial,
  partAtomCreateFromPrimitive,
  partAtomFromFull,
  partAtomsFromFull,
  partAtomsFromFullKeys,
  fullAtomsFromPartKeys,
  returnValFromPartKey,
  returnValFromFullAtom,
  returnValsFromPartKeys,
  returnValsFromFullAtoms,
  returnValsFromFullKeys
})
